import { useState } from "react";
// Components
import {
  MainDiv,
  SectionTitle,
  SectionText,
  ClinicalDataDiv,
  LegendsDiv,
  LegendDiv,
  LegendColor,
  StatusDiv,
  StatusText,
  StatusImg,
  MainPlotDiv,
  ActionsDiv,
  ActionButton,
  ActionImg,
  ActionTitle,
} from "./styles";
import Loader from "../../../../../loader";
import RadarPlot from "../radarPlot";
import Popup from "../../../../../popup";
import ClinicalScoreList from "../clinicalScoreList";
import EditClinicalScore from "../editClinicalScore";
import DetailedClinicalScores from "../detailedClinicalScores";
// Assets
import EditBlueIco from "../../../../../../assets/icons/btn_edit_user_blue.svg";
import InfoIco from "../../../../../../assets/icons/ico_info_blue.svg";
import WarningIco from "../../../../../../assets/icons/ico_warning_white.svg";

const ClinicalScores = ({
  patientData,
  clinicalScores,
  isLoadingClinicalScores,
  currentClinicalScore,
  setCurrentClinicalScore,
  showEditClinicalScore,
  setShowEditClinicalScore,
  refreshClinicalScores,
  setRefreshClinicalScores,
}) => {
  const [showClinicalScoresList, setShowClinicalScoresList] = useState(false);
  const [showDetailedClinicalScores, setShowDetailedClinicalScores] = useState(false);

  const renderLegends = (key) => {
    const currentLegend = clinicalScores.find((clinicalScore) => clinicalScore.KEY === key);

    if (currentLegend?.STATUS && currentLegend?.STATUS !== "WAITING") {
      const mapLegendInfo = {
        pre: {
          background: "#F7931E",
          title: "Baseline",
        },
        END_OF_THERAPY: {
          background: "#26A9B6",
          title: "End of therapy",
        },
        FOLLOW_UP: {
          background: "#15C",
          title: "Follow up",
        },
      };

      const { background, title } = mapLegendInfo[key];

      return (
        <LegendDiv>
          <LegendColor background={background} />
          <SectionText>{title}</SectionText>
          {currentLegend?.STATUS === "MISSING_DATA" && (
            <StatusDiv margin="0 20px" background="#F15A24">
              <StatusText>Missing data</StatusText>
              <StatusImg src={WarningIco} alt="warning-ico" />
            </StatusDiv>
          )}
        </LegendDiv>
      );
    }
  };

  const renderRadarPlot = () => {
    let datasets = [];
    const initialLabels = ["Fatigue", "Motor", "Cognition", "Depression", "Pain"];
    const isNegativeLabel = (label) => {
      const negativeLabels = new Set(["Fatigue", "Depression", "Pain"]);
      return negativeLabels.has(label);
    };
    const studyOrder = ["Baseline", "End of therapy", "Follow up"];

    let clinicalScoresValues = [];

    const mapStudyInfo = {
      pre: {
        label: "Baseline",
        background: "rgba(247, 147, 30, 0.2)",
        border: "#F7931E",
      },
      END_OF_THERAPY: {
        label: "End of therapy",
        background: "rgba(38, 169, 182, 0.2)",
        border: "#26A9B6",
      },
      FOLLOW_UP: {
        label: "Follow up",
        background: "rgba(17, 85, 204, 0.2)",
        border: "#15C",
      },
    };

    for (const clinicalScore of clinicalScores) {
      if (clinicalScore?.STATUS && clinicalScore?.STATUS !== "WAITING") {
        const { label, background, border } = mapStudyInfo[clinicalScore.KEY];

        const motor =
          clinicalScore?.CLINICAL_SCORES?.ARAT?.Grasp &&
          clinicalScore?.CLINICAL_SCORES?.ARAT?.Grip &&
          clinicalScore?.CLINICAL_SCORES?.ARAT?.GrossMovement &&
          clinicalScore?.CLINICAL_SCORES?.ARAT?.Pinch
            ? (clinicalScore.CLINICAL_SCORES.ARAT.Grasp +
                clinicalScore.CLINICAL_SCORES.ARAT.Grip +
                clinicalScore.CLINICAL_SCORES.ARAT.GrossMovement +
                clinicalScore.CLINICAL_SCORES.ARAT.Pinch) /
              57
            : NaN;

        const cognition =
          clinicalScore?.CLINICAL_SCORES?.MoCA?.Abstraction &&
          clinicalScore?.CLINICAL_SCORES?.MoCA?.Attention &&
          clinicalScore?.CLINICAL_SCORES?.MoCA?.DelayedRecall &&
          clinicalScore?.CLINICAL_SCORES?.MoCA?.Language &&
          clinicalScore?.CLINICAL_SCORES?.MoCA?.Naming &&
          clinicalScore?.CLINICAL_SCORES?.MoCA?.Orientation &&
          clinicalScore?.CLINICAL_SCORES?.MoCA?.["Visuospatial/Executive"]
            ? (clinicalScore.CLINICAL_SCORES.MoCA.Abstraction +
                clinicalScore.CLINICAL_SCORES.MoCA.Attention +
                clinicalScore.CLINICAL_SCORES.MoCA.DelayedRecall +
                clinicalScore.CLINICAL_SCORES.MoCA.Language +
                clinicalScore.CLINICAL_SCORES.MoCA.Naming +
                clinicalScore.CLINICAL_SCORES.MoCA.Orientation +
                clinicalScore.CLINICAL_SCORES.MoCA["Visuospatial/Executive"]) /
              30
            : NaN;

        let data = [
          clinicalScore?.CLINICAL_SCORES?.Other?.Fatigue ? clinicalScore.CLINICAL_SCORES.Other.Fatigue / 63 : NaN,
          motor,
          cognition,
          clinicalScore?.CLINICAL_SCORES?.Other?.Depression ? clinicalScore.CLINICAL_SCORES.Other.Depression / 63 : NaN,
          clinicalScore?.CLINICAL_SCORES?.Other?.Pain ? clinicalScore.CLINICAL_SCORES.Other.Pain / 10 : NaN,
        ];

        clinicalScoresValues.push({ key: label, data });

        datasets.push({
          label: label,
          data: data,
          backgroundColor: background,
          borderColor: border,
          borderWidth: 1,
        });
      }
    }

    const clinicalScoresOrdered = clinicalScoresValues.sort((a, b) => studyOrder.indexOf(a.key) - studyOrder.indexOf(b.key));

    const filteredClinicalScores = clinicalScoresOrdered.length > 2 ? clinicalScoresOrdered.slice(-2) : clinicalScoresOrdered;

    const LabelsInfo = initialLabels.map((label, i) => {
      if (filteredClinicalScores.length < 2) {
        return {
          label,
          labelColor: "#000",
        };
      }

      const firstValue = filteredClinicalScores[0].data[i];
      const secondValue = filteredClinicalScores[1].data[i];

      if (isNaN(firstValue) || isNaN(secondValue)) {
        return {
          label,
          labelColor: "#000",
        };
      }

      if (firstValue < secondValue) {
        return {
          label: `${label} ▲`,
          labelColor: isNegativeLabel(label) ? "#D9434E" : "#22B573",
        };
      }

      if (firstValue > secondValue) {
        return {
          label: `${label} ▼`,
          labelColor: isNegativeLabel(label) ? "#22B573" : "#D9434E",
        };
      }

      return {
        label,
        labelColor: "#000",
      };
    });

    const data = {
      labels: LabelsInfo.map((label) => label.label),
      datasets: datasets,
    };
    return <RadarPlot data={data} LabelColors={LabelsInfo.map((label) => label.labelColor)} maxScale={1} />;
  };

  const renderClinicalScoreListPopup = () => {
    return (
      <Popup backgroundId="ClinicalScoreList" isShowed={showClinicalScoresList} setIsShowed={setShowClinicalScoresList}>
        <ClinicalScoreList
          clinicalScores={clinicalScores}
          setCurrentClinicalScore={setCurrentClinicalScore}
          setShowEditClinicalScore={setShowEditClinicalScore}
        />
      </Popup>
    );
  };

  const renderEditClinicalScorePopup = () => {
    return (
      <Popup isShowed={showEditClinicalScore} setIsShowed={setShowEditClinicalScore}>
        <EditClinicalScore
          patientData={patientData}
          setShowClinicalScoresList={setShowClinicalScoresList}
          clinicalScore={currentClinicalScore}
          setShowEditClinicalScore={setShowEditClinicalScore}
          refreshClinicalScores={refreshClinicalScores}
          setRefreshClinicalScores={setRefreshClinicalScores}
        />
      </Popup>
    );
  };

  const renderDetailedClinicalScoresPopup = () => {
    return (
      <Popup isShowed={showDetailedClinicalScores} setIsShowed={setShowDetailedClinicalScores}>
        <DetailedClinicalScores clinicalScores={clinicalScores} setShowDetailedClinicalScores={setShowDetailedClinicalScores} />
      </Popup>
    );
  };

  const renderActionsButtons = () => {
    return (
      <ActionsDiv>
        <ActionButton onClick={() => setShowClinicalScoresList(true)}>
          <ActionImg src={EditBlueIco} alt="edit-ico" />
          <ActionTitle>Edit clinical Scores</ActionTitle>
        </ActionButton>
        <ActionButton onClick={() => setShowDetailedClinicalScores(true)}>
          <ActionTitle>See details</ActionTitle>
          <ActionImg src={InfoIco} alt="edit-ico" />
        </ActionButton>
      </ActionsDiv>
    );
  };

  return (
    <>
      {isLoadingClinicalScores ? (
        <Loader height={50} />
      ) : (
        <>
          <MainDiv>
            <SectionTitle>Clinical scores</SectionTitle>
            <ClinicalDataDiv>
              <LegendsDiv>
                {renderLegends("pre")}
                {renderLegends("END_OF_THERAPY")}
                {renderLegends("FOLLOW_UP")}
              </LegendsDiv>
              <MainPlotDiv>{renderRadarPlot()}</MainPlotDiv>
              {renderActionsButtons()}
            </ClinicalDataDiv>
          </MainDiv>
          {showClinicalScoresList && renderClinicalScoreListPopup()}
          {showEditClinicalScore && renderEditClinicalScorePopup()}
          {showDetailedClinicalScores && renderDetailedClinicalScoresPopup()}
        </>
      )}
    </>
  );
};

export default ClinicalScores;
